import { useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { SignIn } from './components/SignIn';
import { WorkspaceHome } from './components/WorkspaceHome';
import { CwiWorkspace } from './components/CwiWorkspace';
import { CreatorPartnerWorkspace } from './components/CreatorPartnerWorkspace';
import { AdminWorkspace } from './components/AdminWorkspace';
import { supabase } from './lib/supabase';
import { canWrite, navFor, workspacesFor } from './permissions';
import type { NavKey, Workspace } from './types';

const NAV_LABELS: Record<NavKey, string> = {
  home: 'Home',
  programs: 'Programs',
  cohorts: 'Cohorts',
  participants: 'Participants',
  reports: 'Reports',
  'creator-partners': 'Creator Partners',
  'revenue-payouts': 'Revenue & Payouts',
  resources: 'Resources',
  settings: 'Settings',
  help: 'Help',
};

function Shell() {
  const { profile, signOut } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const workspaces = useMemo(() => profile ? workspacesFor(profile) : [], [profile]);
  const workspace = workspaces.find((item) => item.id === params.workspaceId) ?? workspaces[0];
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    if (workspace && params.workspaceId !== workspace.id) navigate(`/w/${encodeURIComponent(workspace.id)}/home`, { replace: true });
  }, [workspace, params.workspaceId, navigate]);

  if (!profile || !workspace) return <NoAccess email={profile?.email ?? ''} onSignOut={signOut} />;
  const nav = navFor(workspace, profile);
  const activePage = (params.page as NavKey | undefined) ?? 'home';
  if (!nav.includes(activePage)) return <Navigate to={`/w/${encodeURIComponent(workspace.id)}/home`} replace />;

  return (
    <div className="console-shell" data-stakd-theme="light">
      <aside className={mobileNav ? 'sidebar open' : 'sidebar'}>
        <div className="brand-lockup inverse"><strong>STAKD</strong><span>PARTNER OS</span></div>
        <div>
          <p className="side-label">WORKSPACE</p>
          <div className="workspace-list">
            {workspaces.map((item) => <WorkspaceButton key={item.id} workspace={item} selected={item.id === workspace.id} onClick={() => { navigate(`/w/${encodeURIComponent(item.id)}/home`); setMobileNav(false); }} />)}
          </div>
        </div>
        <nav aria-label="Workspace navigation">
          <p className="side-label">NAVIGATION</p>
          {nav.map((key) => (
            <button key={key} className={activePage === key ? 'nav-item active' : 'nav-item'} onClick={() => { navigate(`/w/${encodeURIComponent(workspace.id)}/${key}`); setMobileNav(false); }}>
              <span />{NAV_LABELS[key]}
            </button>
          ))}
        </nav>
        <div className="side-footer"><span>SECURE CONSOLE</span><button onClick={() => void signOut()}>Sign out</button></div>
      </aside>

      <main className="main">
        <header className="topbar">
          <button className="menu-button" onClick={() => setMobileNav((value) => !value)} aria-label="Toggle navigation">☰</button>
          <div className="program-lockup">
            <div className="org-mark">{initials(workspace.name)}</div>
            <div><strong title={workspace.name}>{workspace.name}</strong><span>{workspace.roleLabel} · Powered by STAKD</span></div>
          </div>
          <div className="header-actions">
            <button>Last 30 days</button>
            <span>UPDATED<br />JUST NOW</span>
            <button aria-label="Help">?</button>
            <button aria-label="Notifications">●</button>
            <button className="account" title={profile.email}>{initials(profile.email)}</button>
          </div>
        </header>
        {workspace.kind === 'sponsored'
          ? <CwiWorkspace workspace={workspace} activePage={activePage} writable={canWrite(workspace, profile)} />
          : workspace.kind === 'creator'
            ? <CreatorPartnerWorkspace workspace={workspace} activePage={activePage} writable={canWrite(workspace, profile)} />
            : workspace.kind === 'admin'
              ? <AdminWorkspace workspace={workspace} activePage={activePage} writable={canWrite(workspace, profile)} />
              : activePage === 'home'
                ? <WorkspaceHome workspace={workspace} />
                : <PhasePlaceholder page={NAV_LABELS[activePage]} workspace={workspace} />}
      </main>
    </div>
  );
}

function WorkspaceButton({ workspace, selected, onClick }: { workspace: Workspace; selected: boolean; onClick(): void }) {
  return <button className={selected ? 'workspace-button selected' : 'workspace-button'} onClick={onClick}><strong>{workspace.name}</strong><span>{workspace.roleLabel}</span></button>;
}

function PhasePlaceholder({ page, workspace }: { page: string; workspace: Workspace }) {
  return <div className="page-grid"><section className="panel empty-state"><p className="eyebrow">{workspace.roleLabel.toUpperCase()}</p><h1>{page}</h1><p>This protected route and permission boundary are ready. Feature content lands in its scheduled phase after Foundation security review.</p></section></div>;
}

function NoAccess({ email, onSignOut }: { email: string; onSignOut(): Promise<void> }) {
  const [displayName, setDisplayName] = useState('');
  const [handle, setHandle] = useState('');
  const [community, setCommunity] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function apply() {
    if (!displayName.trim()) return setNote('Your name is required.');
    setBusy(true); setNote('');
    const { error } = await supabase.rpc('submit_partner_application', {
      p_display_name: displayName.trim(), p_email: email, p_handle_preference: handle.trim() || null,
      p_application_data: { community: community.trim() || null },
    });
    setBusy(false);
    setNote(error ? error.message : 'Application received. STAKD aims to complete fit review within the configured review window.');
  }
  return <main className="signin-stage" data-stakd-theme="light"><section className="signin-card"><p className="eyebrow">CREATOR PARTNER PROGRAM</p><h1>Apply to partner with STAKD</h1><p className="muted">Tell us who you serve. Applying does not unlock referral tools; those become available after review and onboarding are complete.</p><div className="settings-form"><label>Name<input value={displayName} onChange={(event)=>setDisplayName(event.target.value)} /></label><label>Preferred handle<input value={handle} onChange={(event)=>setHandle(event.target.value.toUpperCase())} /></label><label>Community or audience<input value={community} onChange={(event)=>setCommunity(event.target.value)} /></label>{note ? <div className="muted">{note}</div> : null}<button className="primary-button" disabled={busy} onClick={()=>void apply()}>{busy ? 'Submitting…' : 'Submit application'}</button><button className="link-button" onClick={() => void onSignOut()}>Sign out</button></div></section></main>;
}

function initials(value: string) {
  return value.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join('') || 'ST';
}

export default function App() {
  const { session, loading, error } = useAuth();
  if (loading) return <main className="loading-stage" data-stakd-theme="light"><div className="loading-mark">STAKD</div><p>Resolving secure access…</p></main>;
  if (!session) return <SignIn />;
  if (error) return <main className="signin-stage" data-stakd-theme="light"><section className="signin-card"><p className="eyebrow">ACCESS CHECK FAILED</p><h1>We could not resolve your roles</h1><div className="error">{error}</div></section></main>;
  return <Routes><Route path="/w/:workspaceId/:page" element={<Shell />} /><Route path="*" element={<Navigate to="/w/pending/home" replace />} /></Routes>;
}
